import { CheckCircle, Download } from '@mui/icons-material';
import {
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  type ExtendedDispatchPlanHistoryResponseItemType,
  type DispatchPlanIds,
} from '../DispatchPlanHistory';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import { exportPlanDataToExcel } from 'views/FleetPlanner/utils/exportToExcel';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { useState } from 'react';
import { type Dayjs } from 'dayjs';
import { useAppSelector } from 'store/hook';
import { SetTodayPlanConfirmationModal } from '../modal/SetTodayPlanConfirmationPlan';
import { type DispatchSinglePlanSummaryType } from 'views/FleetPlanner/DispatchSummary/types/dispatch-summary.types';
import { useTranslation } from 'react-i18next';
import useAppTheme from 'hooks/useAppTheme';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const PlanHeader = ({
  planSummary,
  activePlan,
  selectedPlan,
  handleSetDispatchSummaryPlan,
  inputData,
}: {
  planSummary: DispatchSinglePlanSummaryType;
  activePlan: DispatchPlanIds;
  selectedPlan: ExtendedDispatchPlanHistoryResponseItemType | null;
  handleSetDispatchSummaryPlan: (
    historyDataItem: ExtendedDispatchPlanHistoryResponseItemType,
    isActive: boolean,
    setActivePlan: boolean,
    paramSelectedRange: string,
    paramSelectedDate: Dayjs | null
  ) => void;
  inputData: Record<string, any>;
}) => {
  const { t } = useTranslation();
  const { customerTz } = useCustomerInfo();
  const assetsData = useAppSelector((state) => state.assetReducer.assets);

  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    inactiveAssets: string[];
  }>({ open: false, inactiveAssets: [] });

  const handleExportClick = () => {
    const planName = selectedPlan?.planName
      ? `${selectedPlan?.planName}.xlsx`
      : 'approved_schedule.xlsx';

    exportPlanDataToExcel(
      planSummary.planData,
      planName,
      'configured',
      customerTz
    );
  };

  const handleSetTodayClick = () => {
    const inputAssets = Array.isArray(inputData?.assets)
      ? inputData?.assets
      : [];
    const inactiveAssetsInPlan = assetsData
      ?.filter(
        (asset) => inputAssets.includes(asset.bumperNumber) && !asset.isActive
      )
      ?.map((asset) => asset.bumperNumber);

    setConfirmState({ open: true, inactiveAssets: inactiveAssetsInPlan });
  };

  const handleConfirmSetPlan = () => {
    setConfirmState({ open: false, inactiveAssets: [] });
    if (selectedPlan) {
      handleSetDispatchSummaryPlan(selectedPlan, true, true, 'Today', null);
    }
  };

  const isActivePlan =
    activePlan.historyId !== '' &&
    selectedPlan?.historyId !== '' &&
    activePlan.historyId === selectedPlan?.historyId;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 3,
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Typography
            sx={{ fontWeight: 700, fontSize: '1.5rem' }}
            maxWidth={'400px'}
            noWrap
            title={selectedPlan?.planName ?? ' '}
          >
            {selectedPlan?.planName ?? ''}
          </Typography>
          {isActivePlan && (
            <Chip
              label={t('today_s_active_plan')}
              size="small"
              color="success"
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {!isActivePlan && (
            <CustomLoadingButton
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={handleSetTodayClick}
            >
              {`Set as Today's Plan`}
            </CustomLoadingButton>
          )}
          <CustomLoadingButton
            variant="contained"
            color="primary"
            sx={{
              px: 2,
              borderRadius: 2,
              height: '36px',
            }}
            startIcon={<Download />}
            onClick={handleExportClick}
          >
            Export
          </CustomLoadingButton>
        </Box>
      </Box>
      {confirmState.open && (
        <SetTodayPlanConfirmationModal
          open={confirmState.open}
          inactiveAssets={confirmState.inactiveAssets}
          onClose={() => {
            setConfirmState({ open: false, inactiveAssets: [] });
          }}
          onConfirm={handleConfirmSetPlan}
        />
      )}
    </>
  );
};

const BlendRatioCard = ({
  dumpKey,
  ratios,
  loadingAreaKeys,
}: {
  dumpKey: string;
  ratios: unknown;
  loadingAreaKeys: unknown;
}) => {
  const { t } = useTranslation();
  const validatedRatios: number[] = Array.isArray(ratios)
    ? ratios.filter((val): val is number => typeof val === 'number')
    : [];

  const validatedLoadingKeys: string[] = Array.isArray(loadingAreaKeys)
    ? loadingAreaKeys.filter((key): key is string => typeof key === 'string')
    : [];

  if (validatedRatios.length === 0) {
    return (
      <Grid item xs={12} md={6}>
        <Typography color="error" variant="body2">
          {t('invalid_or_empty_mixture_ratios_for')} {dumpKey}
        </Typography>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <Typography variant="body2" color={'text.secondary'}>
        {t('blend_ratio')}
        {dumpKey})
      </Typography>
      <Box display="flex" gap={1} mt={1} flexWrap="wrap" width={'100%'}>
        {validatedRatios.map((ratioValue, index) => {
          const label = validatedLoadingKeys[index] ?? `L${index}`; // fallback if label missing

          return (
            <Box
              key={`${dumpKey}-${label}`}
              sx={{
                backgroundColor: 'customBackground.section',
                borderRadius: 2,
                minWidth: 60,
                textAlign: 'center',
                px: 2,
                py: 1,
              }}
            >
              {`${label}: ${(ratioValue * 100).toFixed(0)}%`}
            </Box>
          );
        })}
      </Box>
    </Grid>
  );
};

const TravelTimesCard = ({ distances }: { distances: Record<string, any> }) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const validEntries: Array<{ from: string; to: string; minutes: number }> = [];

  if (distances && typeof distances === 'object' && !Array.isArray(distances)) {
    for (const [key, value] of Object.entries(distances)) {
      try {
        // Keys are JSON strings like '["L0","D0"]'
        const parsed = JSON.parse(key);
        if (
          Array.isArray(parsed) &&
          parsed.length === 2 &&
          typeof parsed[0] === 'string' &&
          typeof parsed[1] === 'string' &&
          typeof value === 'number'
        ) {
          validEntries.push({ from: parsed[0], to: parsed[1], minutes: value });
        }
      } catch (error) {
        console.log(error);
      }
    }
  }

  if (validEntries.length === 0) {
    return (
      <Paper style={{ padding: 16, backgroundColor: palette.paperBackground }}>
        <Typography color="error">
          {t('no_valid_distance_data_found')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 2,
        height: '100%',
        border: '1px solid red',
        borderColor: 'borderColor.grey',
      }}
      elevation={0}
    >
      <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
        {t('site_travel_times')}
      </Typography>

      <Box>
        {validEntries.map((row, index) => (
          <Box
            key={`${row?.from || index}-${row?.to || index + 1}`}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              py: 1,
              borderBottom:
                index === validEntries.length - 1 ? 'none' : `1px solid`,
              borderColor: 'borderColor.grey',
            }}
          >
            <Typography
              color={'text.secondary'}
              sx={{
                fontSize: '14px',
              }}
            >
              {`${row?.from} → ${row?.to}`}:
            </Typography>
            <Typography
              sx={{ fontWeight: 500, fontSize: '14px' }}
            >{`${row?.minutes} mins`}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const AssetAssignment = ({ inputData }: { inputData: Record<string, any> }) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  if (!inputData || typeof inputData !== 'object') {
    return (
      <Paper style={{ padding: 16, backgroundColor: palette.paperBackground }}>
        <Typography color="error">{t('invalid_asset_data_object')}</Typography>
      </Paper>
    );
  }

  const bumperNumbers = Array.isArray(inputData?.assets)
    ? inputData?.assets
    : [];
  const startTimes = Array.isArray(inputData?.truck_start_times)
    ? inputData.truck_start_times
    : [];
  const endTimes = Array.isArray(inputData?.truck_end_times)
    ? inputData.truck_end_times
    : [];
  const lunchTimes = Array.isArray(inputData?.lunch_time_target)
    ? inputData.lunch_time_target
    : [];
  const capacities = Array.isArray(inputData?.truck_capacities)
    ? inputData.truck_capacities
    : [];
  const maxCapacities = Array.isArray(inputData?.truck_max_capacities)
    ? inputData.truck_max_capacities
    : [];

  const lunchDurations = Array.isArray(inputData?.lunch_break_minutes)
    ? inputData.lunch_break_minutes
    : [];
  const lengths = [
    startTimes.length,
    endTimes.length,
    lunchTimes.length,
    capacities.length,
    maxCapacities.length,
  ];

  const allEqual = lengths.every((len) => len === lengths[0]);
  if (!allEqual) {
    return (
      <Paper style={{ padding: 16, backgroundColor: palette.paperBackground }}>
        <Typography color="error">
          {t('mismatch_in_truck_data_arrays_all_asset_arrays_must_have_the')}
        </Typography>
      </Paper>
    );
  }

  const totalAssets = startTimes.length;

  if (totalAssets === 0) {
    return (
      <Paper style={{ padding: 16, backgroundColor: palette.paperBackground }}>
        <Typography color="error">{t('no_trucks_found_in_dataset')}</Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid red',
        borderColor: 'borderColor.grey',
      }}
      elevation={0}
    >
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {t('asset_assignment')}
      </Typography>

      <Box sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  color: 'text.secondary',
                  textAlign: 'left',
                }}
              >
                {t('asset')}
              </TableCell>
              <TableCell align="center" sx={{ color: 'text.secondary' }}>
                {t('availability')}
              </TableCell>
              <TableCell
                sx={{
                  color: 'text.secondary',
                  textAlign: 'center',
                }}
              >
                Lunch
              </TableCell>
              <TableCell
                sx={{
                  color: 'text.secondary',
                  textAlign: 'center',
                }}
              >
                {t('capacity_tons')}
              </TableCell>
              <TableCell
                sx={{
                  color: 'text.secondary',
                  textAlign: 'right',
                }}
              >
                {t('max_capacity_tons')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {startTimes?.map((start, index) => {
              const bumperNumber = bumperNumbers[index];
              const end = endTimes[index];
              const lunch = lunchTimes[index];
              const capacity = capacities[index];
              const maxCapacity = maxCapacities[index];
              const lunchDuration = lunchDurations[index];
              return (
                <TableRow
                  key={bumperNumber}
                  sx={{ borderColor: `borderColor.grey` }}
                >
                  <TableCell sx={{ fontWeight: 500 }} align="left">
                    {bumperNumber}
                  </TableCell>
                  <TableCell align="center">{`${start} → ${end}`}</TableCell>
                  <TableCell align="center" sx={{ textAlign: 'center' }}>
                    {`${lunch}(${lunchDuration} mins)`}
                  </TableCell>
                  <TableCell align="center" sx={{ textAlign: 'center' }}>
                    {capacity}
                  </TableCell>
                  <TableCell align="right" sx={{ textAlign: 'right' }}>
                    {maxCapacity}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
};

export const PlanDetailView = ({
  planSummary,
  activePlan,
  selectedPlan,
  handleSetDispatchSummaryPlan,
}: {
  planSummary: DispatchSinglePlanSummaryType;
  activePlan: DispatchPlanIds;
  selectedPlan: ExtendedDispatchPlanHistoryResponseItemType | null;
  handleSetDispatchSummaryPlan: (
    historyDataItem: ExtendedDispatchPlanHistoryResponseItemType,
    isActive: boolean,
    setActivePlan: boolean,
    paramSelectedRange: string,
    paramSelectedDate: Dayjs | null
  ) => void;
}) => {
  const { t } = useTranslation();
  const inputData = planSummary?.inputData || {};

  return (
    <>
      <PlanHeader
        planSummary={planSummary}
        activePlan={activePlan}
        selectedPlan={selectedPlan}
        handleSetDispatchSummaryPlan={handleSetDispatchSummaryPlan}
        inputData={inputData}
      />

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Paper
            sx={{
              padding: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'borderColor.grey',
            }}
            elevation={0}
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {t('fleet_level_input')}
            </Typography>
            <Grid container spacing={2} width={'100%'}>
              {Object.entries(inputData?.mixture_ratios || {}).map(
                ([dumpKey, ratios]) => (
                  <BlendRatioCard
                    key={dumpKey}
                    dumpKey={dumpKey}
                    ratios={ratios}
                    loadingAreaKeys={inputData?.loading_areas || []}
                  />
                )
              )}
            </Grid>

            <Box>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mb: 1 }}
              >
                Tolerances
              </Typography>
              <Box
                sx={{
                  bgcolor: 'customBackground.section',
                  borderRadius: 2,
                  py: 1.2,
                  px: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography sx={{ color: 'text.secondary' }}>
                    {t('idling_percentage')}
                  </Typography>
                  <Typography>{`${
                    inputData?.excess_threshold * 100 || 'N/A'
                  }%`}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: 'text.secondary' }}>
                    {t('lunch')}
                  </Typography>
                  <Typography>{`${
                    inputData?.lunch_tolerance_minutes || 0
                  } mins`}</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} lg={6}>
          <TravelTimesCard distances={inputData?.distances_minutes || {}} />
        </Grid>
        <Grid item xs={12}>
          <AssetAssignment inputData={inputData} />
        </Grid>
      </Grid>
    </>
  );
};
